// "Backend" del MVP: estado en memoria persistido en localStorage.
//
// - Los datos crudos viven en data/seed.ts (extraídos de la D1 real).
// - Las ediciones del usuario (cambiar área, rol, %, activo/inactivo, etc.)
//   se guardan en localStorage y sobreviven recargas.
// - El botón "Reset demo" llama a resetDemo() y borra todo, volviendo al seed.
//
// Cuando exista un backend real, esta capa desaparece: api.ts vuelve a hacer
// fetch() y se borra data/ + store.ts. Los componentes no cambian.

import { seedEmployees, seedAbsences, seedAdminDays } from '../data/seed';
import { HOLIDAYS_CL_2026 } from '../data/holidays-cl';
import { getCustomDays, resetCustomDays } from './nonWorkingDaysStore';
import type { SeedEmployee, SeedTimeEntry } from '../data/types';

// Los time entries crudos (4+ MB) viven en public/data/seed-time-entries.json y
// se cargan con fetch al iniciar la app: así NO entran al bundle ni funden la
// compilación. initStore() debe completarse antes de pedir reportes.
let seedTimeEntries: SeedTimeEntry[] = [];
let loaded = false;

export async function initStore(): Promise<void> {
  if (loaded) return;
  const res = await fetch(`${process.env.PUBLIC_URL || ''}/data/seed-time-entries.json`);
  if (!res.ok) throw new Error('No se pudieron cargar los datos de horas');
  seedTimeEntries = (await res.json()) as SeedTimeEntry[];
  loaded = true;
}

const STORAGE_KEY = 'fycom_demo_employees';

// Solo los empleados son editables en el demo. Time entries / ausencias / días adm
// son inmutables (vienen del seed) — son el "histórico crudo".
function loadEmployees(): SeedEmployee[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as SeedEmployee[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    // localStorage corrupto: caer al seed.
  }
  // Clon profundo del seed para no mutar el módulo importado.
  return seedEmployees.map((e) => ({ ...e }));
}

let employees: SeedEmployee[] = loadEmployees();

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(employees));
  } catch {
    // Sin espacio / modo privado: el demo sigue funcionando en memoria.
  }
}

export function getEmployees(): SeedEmployee[] {
  return employees.map((e) => ({ ...e }));
}

export function updateEmployee(
  id: string,
  updates: Partial<Omit<SeedEmployee, 'id'>>
): SeedEmployee | null {
  const idx = employees.findIndex((e) => e.id === id);
  if (idx === -1) return null;
  employees[idx] = { ...employees[idx], ...updates };
  persist();
  return { ...employees[idx] };
}

// Datos crudos inmutables expuestos para el motor de reportes.
export function getReportData() {
  // Días no laborales de la empresa = feriados legales (día completo) +
  // actividades oficina del usuario (con su portion). Alimentan el descuento de meta.
  const nonWorkingDays = [
    ...HOLIDAYS_CL_2026.map((h) => ({ date: h.date, portion: 'completo' as const })),
    ...getCustomDays().map((d) => ({ date: d.date, portion: d.portion })),
  ];
  return {
    employees,
    timeEntries: seedTimeEntries,
    absences: seedAbsences,
    adminDays: seedAdminDays,
    nonWorkingDays,
  };
}

// Botón "Reset demo": borra ediciones y vuelve al seed original.
export function resetDemo(): void {
  localStorage.removeItem(STORAGE_KEY);
  employees = seedEmployees.map((e) => ({ ...e }));
  resetCustomDays(); // limpia también las actividades oficina del calendario
}
